import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { localStorageItemNames } from "../constants";
import { moduleActivityApi } from "../api/moduleActivity-api";
import {
  MODULE_ACTIVITY_ACTION_EVENT,
} from "../api/base-api";

const HEARTBEAT_INTERVAL_MS = 15000;
const STORAGE_KEY_PREFIX = "cashflow_module_activity";
const configuredIdleThreshold = Number(
  process.env.REACT_APP_IDLE_THRESHOLD_MS,
);
const IDLE_THRESHOLD_MS =
  Number.isFinite(configuredIdleThreshold) && configuredIdleThreshold > 0
    ? configuredIdleThreshold
    : 60000;

const MODULE_NAME_BY_PATH = {
  "": "Dashboard",
  otpscreen: "OTP Verification",
  dashboard: "Dashboard",
  starred: "Starred",
  "fuel-economy": "Fuel Economy",
  "fuel-price": "Fuel Price",
  "manage-companies": "Manage Companies",
  "manage-dealer": "Manage Dealer",
  "manage-company-dealer": "Manage Company Dealer",
  "add-dealer": "Add Dealer",
  "manage-user": "Manage User",
  "manage-vehicle": "Manage Vehicle",
  reports: "Reports",
  "fuel-analysis": "Fuel Analysis",
  "analysis-results": "Analysis Results",
  analysisreport: "Analysis Report",
  profile: "Profile",
  "fleet-list": "Fleet List",
  addfleetlist: "Add Fleet",
  editfleetlist: "Edit Fleet",
  "new-fleetlist": "New Fleet List",
  adduser: "Add User",
  "manage-userdealer": "Manage Dealer User",
  "manage-dealer-companies": "Manage Dealer Companies",
  "funding-newvehicle": "Funding New Vehicle",
  "replacement-schedule": "Replacement Schedule",
  "maintenance-analysis": "Maintenance Analysis",
  "powerbi-report": "Power BI Report",
  "analysis-data": "Analysis Data",
  userqueries: "User Queries",
  helpdesk: "Help Desk",
  "user-queries": "Admin User Queries",
  userlogs: "User Logs",
  "usage-report": "Module Activity Report",
  comparisioncto: "Comparison CTO",
  userguide: "User Guide",
};

const getModuleName = (pathname) => {
  const segment = pathname.split("/").filter(Boolean).pop() || "";
  const knownModuleName = MODULE_NAME_BY_PATH[segment.toLowerCase()];
  if (knownModuleName) return knownModuleName;

  return decodeURIComponent(segment)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
};

const createSessionId = () => {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
    .slice(6, 8)
    .join("")}-${hex.slice(8, 10).join("")}-${hex
    .slice(10)
    .join("")}`;
};

const getOrCreateSession = (storageKey, moduleName) => {
  const savedSession = sessionStorage.getItem(storageKey);
  if (savedSession) {
    try {
      const session = JSON.parse(savedSession);
      if (
        session.sessionId &&
        Number.isFinite(session.idleMilliseconds) &&
        Number.isFinite(session.lastActivityAt) &&
        Number.isFinite(session.lastIdleAccountedAt)
      ) {
        session.moduleName = moduleName;
        session.activityTitle =
          session.activityTitle || `Viewing ${moduleName}`;
        session.instanceId = createSessionId();
        return session;
      }
    } catch (error) {
      console.warn("Discarding invalid module activity state.", error);
    }
    sessionStorage.removeItem(storageKey);
  }

  const now = Date.now();
  return {
    sessionId: createSessionId(),
    instanceId: createSessionId(),
    moduleName,
    activityTitle: `Viewing ${moduleName}`,
    idleMilliseconds: 0,
    lastActivityAt: now,
    lastIdleAccountedAt: now,
  };
};

const persistSession = (storageKey, session) => {
  sessionStorage.setItem(storageKey, JSON.stringify(session));
};

const accountForIdleTime = (session, now) => {
  const idleStart = session.lastActivityAt + IDLE_THRESHOLD_MS;
  const accountedFrom = Math.max(idleStart, session.lastIdleAccountedAt);
  if (now > accountedFrom) {
    session.idleMilliseconds += now - accountedFrom;
  }
  session.lastIdleAccountedAt = Math.max(session.lastIdleAccountedAt, now);
};

const classifyAction = (label) => {
  if (/\b(delete|remove)\b/i.test(label)) return "Delete record";
  if (/\b(edit|update)\b/i.test(label)) return "Update record";
  if (/\b(create|add|new|save)\b/i.test(label)) return "Create or save record";
  if (/\b(view|open|details?)\b/i.test(label)) return "View record";
  if (/\b(search|filter)\b/i.test(label)) return "Search records";
  return "User interaction";
};

const getInteractionLabel = (target) => {
  if (!(target instanceof Element)) return null;
  const interactiveElement = target.closest(
    'button, [role="button"], [role="menuitem"], input[type="submit"], input[type="button"]',
  );
  if (!interactiveElement) return null;

  return [
    interactiveElement.getAttribute("aria-label"),
    interactiveElement.getAttribute("title"),
    interactiveElement.textContent,
    interactiveElement.value,
  ]
    .filter(Boolean)
    .join(" ")
    .trim()
    .slice(0, 160);
};

const getFormActivityTitle = (label, moduleName) => {
  const action = label
    .match(/\b(add|new|edit|view|details?)\b/i)?.[1]
    ?.toLowerCase();
  if (!action) return null;

  const namedEntity = label.match(/\b(?:add|new|edit)\s+(.+)/i)?.[1]
    ?.replace(/\b(form|record|item)\b.*$/i, "")
    .trim();
  const moduleEntity =
    {
      "Fleet List": "Fleet",
      "New Fleet List": "Fleet",
      "Manage Companies": "Company",
      "Manage Company Dealer": "Dealer Company",
      "Manage Dealer Companies": "Dealer Company",
      "Manage Dealer User": "User",
      "Manage User": "User",
      "Manage Vehicle": "Vehicle",
    }[moduleName] ||
    moduleName
      .replace(/^Manage\s+/i, "")
      .replace(/ies$/i, "y")
      .replace(/s$/i, "");
  const entity = namedEntity || moduleEntity;

  if (action === "edit") return `${entity} form opened for editing`;
  if (action === "view" || action.startsWith("detail")) {
    return `${entity} form opened for viewing`;
  }
  return `${entity} form opened`;
};

export const ModuleActivityTracker = () => {
  const location = useLocation();
  const latestPathRef = useRef(location.pathname);
  latestPathRef.current = location.pathname;

  useEffect(() => {
    const userId = localStorage.getItem(localStorageItemNames.userID);
    const token = localStorage.getItem(localStorageItemNames.user_token);
    if (!token || !/^[1-9]\d*$/.test(userId || "")) return undefined;

    const moduleName = getModuleName(location.pathname);
    const storageKey = `${STORAGE_KEY_PREFIX}:${userId}:${location.pathname}`;
    const session = getOrCreateSession(storageKey, moduleName);
    persistSession(storageKey, session);
    let started = false;
    let closed = false;
    let startRequest;
    let updateRequest;

    const payload = () => ({
      userId: Number(userId),
      sessionId: session.sessionId,
      instanceId: session.instanceId,
      moduleName: session.moduleName,
      activityTitle: session.activityTitle,
      idleTimeMilliseconds: Math.floor(session.idleMilliseconds),
    });

    const recordActivityEvent = async (activityTitle) => {
      try {
        await moduleActivityApi.recordEvent(
          moduleName,
          activityTitle,
          userId,
          token,
        );
      } catch (error) {
        console.error("Unable to record module activity event.", error);
      }
    };

    const start = async () => {
      if (started) return;
      if (!startRequest) {
        startRequest = moduleActivityApi.start(payload(), token).then(() => {
          started = true;
        });
      }
      try {
        await startRequest;
      } catch (error) {
        startRequest = null;
        throw error;
      }
    };

    const update = async () => {
      if (closed) return;
      if (updateRequest) return updateRequest;
      updateRequest = (async () => {
        accountForIdleTime(session, Date.now());
        persistSession(storageKey, session);
        try {
          await start();
          await moduleActivityApi.heartbeat(payload(), token);
        } catch (error) {
          console.error("Unable to update module activity tracking.", error);
          if (error.status === 401) {
            closed = true;
            window.clearInterval(heartbeatId);
          }
        }
      })();
      try {
        await updateRequest;
      } finally {
        updateRequest = null;
      }
    };

    const close = async (keepalive = false) => {
      if (closed) return;
      closed = true;
      if (updateRequest) await updateRequest;
      accountForIdleTime(session, Date.now());
      persistSession(storageKey, session);
      try {
        await start();
        await moduleActivityApi.exit(payload(), token, keepalive);
      } catch (error) {
        console.error("Unable to close module activity tracking.", error);
      }
    };

    const markActivity = (title) => {
      const now = Date.now();
      accountForIdleTime(session, now);
      session.lastActivityAt = now;
      session.lastIdleAccountedAt = now;
      if (title) session.activityTitle = title;
    };

    const handleInteraction = (event) => {
      const label = getInteractionLabel(event.target);
      if (label) {
        markActivity(classifyAction(label));
        const formActivityTitle = getFormActivityTitle(label, moduleName);
        if (formActivityTitle) {
          void recordActivityEvent(formActivityTitle);
        }
      } else {
        markActivity();
      }
      persistSession(storageKey, session);
    };

    const handleModuleAction = (event) => {
      const activityTitle = event.detail?.activityTitle;
      if (typeof activityTitle === "string" && activityTitle.trim()) {
        void recordActivityEvent(activityTitle.trim());
      }
    };

    const handleSimpleActivity = () => markActivity();

    const handleFormSubmit = () => {
      markActivity("Submit form");
      persistSession(storageKey, session);
    };

    const handlePageHide = () => {
      void close(true);
    };

    const activityEvents = [
      "pointermove",
      "keydown",
      "click",
      "scroll",
      "touchstart",
    ];
    activityEvents.forEach((eventName) =>
      document.addEventListener(
        eventName,
        eventName === "click" ? handleInteraction : handleSimpleActivity,
        { capture: true, passive: true },
      ),
    );
    document.addEventListener("submit", handleFormSubmit, true);
    window.addEventListener(
      MODULE_ACTIVITY_ACTION_EVENT,
      handleModuleAction,
    );
    window.addEventListener("pagehide", handlePageHide);

    void start().catch((error) =>
      console.error("Unable to start module activity tracking.", error),
    );
    const heartbeatId = window.setInterval(update, HEARTBEAT_INTERVAL_MS);

    return () => {
      window.clearInterval(heartbeatId);
      activityEvents.forEach((eventName) =>
        document.removeEventListener(
          eventName,
          eventName === "click" ? handleInteraction : handleSimpleActivity,
          true,
        ),
      );
      document.removeEventListener("submit", handleFormSubmit, true);
      window.removeEventListener(
        MODULE_ACTIVITY_ACTION_EVENT,
        handleModuleAction,
      );
      window.removeEventListener("pagehide", handlePageHide);
      void close();
      if (latestPathRef.current !== location.pathname) {
        sessionStorage.removeItem(storageKey);
      }
    };
  }, [location.pathname]);

  return null;
};
